/**
 * Issuing the codes that let somebody create an account.
 *
 * This is the front door of the service, and a different door from any group's:
 * a member inviting a friend to their album decides who sees that album, while
 * this decides who becomes a user at all. Only a platform admin does the second.
 *
 * The code is shown **once**. Only a hash is stored, so there is no screen
 * anywhere that can show it again — which the interface has to say plainly,
 * because a value that looks like every other row in a table does not look like
 * something you had to write down.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useToast } from "../../components/ui/Toast.js";

type UsesChoice = "1" | "5" | "10" | "custom";
type ExpiryChoice = "24" | "72" | "720" | "never";

interface Issued {
  id: string;
  code: string;
  sent: boolean;
}

export function AccessCodesPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const listInput = { limit: 50 };
  const codes = useQuery(trpc.admin.accessCodes.queryOptions(listInput));

  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [send, setSend] = useState(false);

  /*
   * Uses and lifetime are separate choices with separate consequences, so they
   * are asked separately. A code for one person is single-use and short-lived;
   * one pinned in a group chat is neither, and pretending both are the same
   * setting is how somebody ends up sharing a code that never expires without
   * meaning to.
   */
  const [uses, setUses] = useState<UsesChoice>("1");
  const [customUses, setCustomUses] = useState("25");
  const [expiry, setExpiry] = useState<ExpiryChoice>("72");

  const resolvedUses =
    uses === "custom"
      ? Math.min(50, Math.max(1, Number(customUses) || 1))
      : Number(uses);
  const resolvedExpiry = expiry === "never" ? null : Number(expiry);
  /** The one moment the code exists outside somebody's hand. */
  const [issued, setIssued] = useState<Issued | null>(null);

  const create = useMutation({
    mutationFn: () =>
      trpcClient.admin.createAccessCode.mutate({
        email: email.trim() || undefined,
        label: label.trim() || undefined,
        maxUses: resolvedUses,
        expiresInHours: resolvedExpiry,
        send: send && email.trim().length > 0,
      }),
    onSuccess: async (result) => {
      setIssued(result);
      setEmail("");
      setLabel("");
      setSend(false);
      setUses("1");
      setExpiry("72");
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.accessCodes.queryKey(listInput),
      });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      trpcClient.admin.revokeAccessCode.mutate({ id }),
    onSuccess: async () => {
      toast("Code revoked.");
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.accessCodes.queryKey(listInput),
      });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  return (
    <section className="fc-admin__section" aria-labelledby="fc-admin-codes">
      <h2 className="fc-admin__section-title" id="fc-admin-codes">
        Access codes
      </h2>
      <p className="fc-admin__hint">
        A code lets one person create an account. It does not put them in any
        group — a member does that separately, with an invitation of their own.
      </p>

      {/* ── The code, once ── */}
      {issued && (
        <div className="fc-codes__issued" role="status">
          <p className="fc-codes__issued-label">
            {issued.sent
              ? "Sent. Here it is as well, in case the mail does not arrive:"
              : "Copy this now — it cannot be shown again."}
          </p>
          <div className="fc-codes__code">
            <code>{issued.code}</code>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(issued.code)
                  .then(() => toast("Code copied."))
                  .catch(() =>
                    toast("Could not copy — select it by hand.", {
                      variant: "danger",
                    }),
                  );
              }}
            >
              Copy
            </Button>
          </div>
          <Button variant="ghost" onClick={() => setIssued(null)}>
            Done
          </Button>
        </div>
      )}

      {/* ── Issue one ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">Issue a code</h3>

        <Field
          label="Email"
          hint="Optional. Binding a code to an address means an intercepted message is useless to anyone else."
          htmlFor="fc-code-email"
        >
          <Input
            id="fc-code-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="ana@example.com"
            autoComplete="off"
          />
        </Field>

        <Field
          label="Note"
          hint="For your own reference. Not shown to anyone."
          htmlFor="fc-code-label"
        >
          <Input
            id="fc-code-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Ana, from the Tatry trip"
          />
        </Field>

        <div className="fc-codes__choice">
          <span className="fc-codes__choice-label" id="fc-codes-uses">
            Number of uses
          </span>
          <SegmentedControl
            aria-labelledby="fc-codes-uses"
            size="sm"
            value={uses}
            onChange={setUses}
            options={[
              { value: "1", label: "Once" },
              { value: "5", label: "5" },
              { value: "10", label: "10" },
              { value: "custom", label: "Custom" },
            ]}
          />
          {uses === "custom" && (
            <Input
              type="number"
              min={1}
              max={50}
              value={customUses}
              onChange={(event) => setCustomUses(event.target.value)}
              aria-label="Number of uses"
            />
          )}
        </div>

        <div className="fc-codes__choice">
          <span className="fc-codes__choice-label" id="fc-codes-expiry">
            Expires
          </span>
          <SegmentedControl
            aria-labelledby="fc-codes-expiry"
            size="sm"
            value={expiry}
            onChange={setExpiry}
            options={[
              { value: "24", label: "1 day" },
              { value: "72", label: "3 days" },
              { value: "720", label: "30 days" },
              { value: "never", label: "Never" },
            ]}
          />
          {/*
            A code that never expires is a standing invitation. Worth saying,
            since nothing else on the page will ever remind anyone it exists.
          */}
          {expiry === "never" && (
            <p className="fc-admin__hint">
              This will keep working until you revoke it.
            </p>
          )}
        </div>

        <label className="fc-codes__send">
          <input
            type="checkbox"
            checked={send}
            disabled={email.trim().length === 0}
            onChange={(event) => setSend(event.target.checked)}
          />
          <span>
            Email it to them
            {email.trim().length === 0 && " — needs an address above"}
          </span>
        </label>

        <Button onClick={() => create.mutate()} loading={create.isPending}>
          Issue code
        </Button>
        <p className="fc-admin__hint">
          {resolvedUses === 1 ? "One use" : `${String(resolvedUses)} uses`}
          {resolvedExpiry === null
            ? ", no expiry."
            : `, expires in ${resolvedExpiry >= 24 ? `${String(Math.round(resolvedExpiry / 24))} days` : `${String(resolvedExpiry)} hours`}.`}
        </p>
      </div>

      {/* ── What has been issued ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">Issued</h3>

        {codes.isLoading ? (
          <Skeleton height="64px" />
        ) : codes.isError ? (
          <p className="fc-admin__error">{errorMessage(codes.error)}</p>
        ) : !codes.data || codes.data.length === 0 ? (
          <EmptyState
            icon={<TicketIcon />}
            title="No codes issued"
            description="Nobody can create an account until one exists."
          />
        ) : (
          <ul className="fc-codes__list">
            {codes.data.map((row) => {
              const spent = row.usedCount >= row.maxUses;
              const expired =
                row.expiresAt !== null && new Date(row.expiresAt) < new Date();
              const dead = spent || expired || row.revokedAt !== null;

              return (
                <li
                  key={row.id}
                  className="fc-codes__row"
                  data-dead={dead || undefined}
                >
                  <div className="fc-codes__row-main">
                    <span className="fc-codes__row-label">
                      {row.label ?? row.email ?? "Untitled"}
                    </span>
                    <span className="fc-codes__row-email">
                      {row.email ? `${row.email} · ` : ""}
                      {row.maxUses === 1
                        ? "single use"
                        : `${String(row.usedCount)} of ${String(row.maxUses)} used`}
                      {row.expiresAt === null && " · never expires"}
                    </span>
                  </div>

                  {/* The state, said once and in words rather than as a count
                      somebody has to interpret. */}
                  {row.revokedAt ? (
                    <Badge variant="neutral">Revoked</Badge>
                  ) : spent ? (
                    <Badge variant="neutral">Used</Badge>
                  ) : expired ? (
                    <Badge variant="neutral">Expired</Badge>
                  ) : (
                    <Badge variant="success">Unused</Badge>
                  )}

                  {!dead && (
                    <Button
                      variant="ghost"
                      onClick={() => revoke.mutate(row.id)}
                      loading={revoke.isPending && revoke.variables === row.id}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function TicketIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V8Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}
